<template>
    <div>
        <v-form ref="step1Form" v-model="step1Valid">
            <v-card elevation="0" class="px-5 py-4">
                <v-card-text>
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h6 font-weight-bold mb-0 mr-2">{{ $t('finance.loanPrograms.basicInformation') }}</h2>
                        <v-tooltip top color="primary" max-width="350">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon style="color:#0EBF67" v-bind="attrs" v-on="on">
                                    mdi-alert-circle
                                </v-icon>
                            </template>
                            <span>{{ $t('finance.loanPrograms.basicInformationHint') }}</span>
                        </v-tooltip>
                    </div>
                    <v-row class="d-flex align-center justify-between">
                        <v-col cols="12" md="6">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.programTitle') }} <span class="red--text"> *</span>
                            </label>
                            <v-text-field v-model="formData.programTitle" outlined dense
                                :placeholder="$t('finance.loanPrograms.enterProgramTitle')"
                                :rules="[v => !!v || $t('finance.loanPrograms.fieldRequired')]"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">

                        </v-col>
                        <v-col cols="12" md="6">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.attachProgramLogo') }}
                                <span class="red--text"> *</span>
                            </label>
                            <div class="program-logo-upload">
                                <div
                                    class="program-logo-upload__dropzone"
                                    :class="{
                                        'program-logo-upload__dropzone--drag': logoDragOver,
                                        'program-logo-upload__dropzone--error': !!programLogoError,
                                    }"
                                    role="button"
                                    tabindex="0"
                                    :aria-label="$t('finance.loanPrograms.selectProgramLogoImage')"
                                    @click="triggerProgramLogoInput"
                                    @keydown.enter.prevent="triggerProgramLogoInput"
                                    @keydown.space.prevent="triggerProgramLogoInput"
                                    @dragenter.prevent="logoDragOver = true"
                                    @dragover.prevent="logoDragOver = true"
                                    @dragleave.prevent="onLogoDragLeave"
                                    @drop.prevent="onLogoDrop"
                                >
                                    <v-icon class="program-logo-upload__icon" color="primary">mdi-tray-arrow-up</v-icon>
                                    <div class="program-logo-upload__copy">
                                        <span class="program-logo-upload__cta" role="presentation">
                                            {{ $t('finance.loanPrograms.selectFileUploadProgramLogo') }}
                                        </span>
                                        <p class="program-logo-upload__hint">{{ $t('finance.loanPrograms.orDragDrop') }}</p>
                                    </div>
                                </div>
                                <input
                                    ref="fileInput"
                                    type="file"
                                    accept="image/*"
                                    class="program-logo-upload__input"
                                    @change="handleFileSelect"
                                />
                                <div v-if="programLogoError" class="error--text caption mt-1">{{ programLogoError }}</div>
                                <div v-if="logoShowPreviewTile" class="program-logo-upload__preview">
                                    <div class="program-logo-upload__tile">
                                        <div class="program-logo-upload__toolbar">
                                            <button
                                                type="button"
                                                class="program-logo-upload__icon-btn"
                                                :title="$t('Download')"
                                                :aria-label="$t('Download')"
                                                @click.stop="downloadProgramLogo"
                                            >
                                                <v-icon small class="program-logo-upload__toolbar-icon">mdi-download</v-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="program-logo-upload__icon-btn"
                                                :title="$t('finance.loanPrograms.removeFile')"
                                                :aria-label="$t('finance.loanPrograms.removeFile')"
                                                @click.stop="removeProgramLogo"
                                            >
                                                <v-icon small class="program-logo-upload__toolbar-icon">mdi-delete</v-icon>
                                            </button>
                                        </div>
                                        <div class="program-logo-upload__tile-body">
                                            <img
                                                v-if="logoDisplayUrl"
                                                :src="logoDisplayUrl"
                                                alt=""
                                                class="program-logo-upload__thumb"
                                            />
                                            <span class="program-logo-upload__ext">{{ logoExtensionLabel }}</span>
                                        </div>
                                    </div>
                                    <div class="program-logo-upload__filename" :title="logoFileTitle">
                                        {{ logoTruncatedFileName }}
                                    </div>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" md="6"></v-col>

                    </v-row>
                </v-card-text>
            </v-card>
            <v-card elevation="0" class="px-5 py-4 my-4">
                <v-card-text>
                    <div class="text-h6 mb-4 d-flex align-center">
                        {{ $t('finance.loanPrograms.loanProgramSettings') }}
                        <v-tooltip top color="primary" max-width="350">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon class="icon-with-background primary--text pl-2" style="color:#0EBF67"
                                    v-bind="attrs" v-on="on">
                                    mdi-alert-circle
                                </v-icon>
                            </template>
                            <span>{{ $t('finance.loanPrograms.loanProgramSettingsHint') }}</span>
                        </v-tooltip>
                    </div>
                    <v-row>
                        <v-col cols="12" md="6">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.bank') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-select 
                            v-model="formData.bank" 
                            :items="bankOptions" 
                            item-text="name" 
                            item-value="value"
                            outlined dense :placeholder="$t('finance.loanPrograms.selectBank')"
                            hide-details="auto"
                            :rules="[requiredRule]"
                            append-icon="mdi-chevron-down">
                            </v-select>
                        </v-col>
                    </v-row>
                    
                    <h3 class="text-subtitle-1 font-weight-medium mt-6 mb-3">
                        {{ $t('finance.loanPrograms.applicationApproval') }}
                        <span class="red--text"> *</span>
                    </h3>
                    <v-row>
                        <v-col cols="12">
                            <div class="d-flex align-center gap-4">
                                <div class="d-flex align-center">
                                    <v-radio-group
                                        v-model="formData.applicationApproval"
                                        row
                                        hide-details="auto"
                                        class="mt-0"
                                        :rules="[requiredRule]"
                                    >
                                        <v-radio label="Cooperative" value="COOPERATIVE" color="primary" class="mr-4"></v-radio>
                                        <v-radio label="Bank" value="BANK" color="primary"></v-radio>
                                    </v-radio-group>
                                </div>
                            </div>
                        </v-col>
                    </v-row>

                    <h3  v-if="isBankApprovalSelected" class="text-subtitle-1 font-weight-medium mt-6 mb-3">
                        {{ $t('finance.loanPrograms.applicationProcess') }}
                    </h3>
                    <v-row v-if="isBankApprovalSelected">
                        <v-col cols="12">
                            <v-checkbox v-model="formData.settingsConclude" hide-details color="primary"
                                class="mt-0">
                                <template v-slot:label>
                                    <span class="text-body-2">{{ $t('finance.loanPrograms.concludeBeforeBank') }}</span>
                                </template>
                            </v-checkbox>
                            <v-checkbox v-model="formData.settingsAllowBank" hide-details color="primary"
                                class="mt-0">
                                <template v-slot:label>
                                    <span class="text-body-2">{{ $t('finance.loanPrograms.allowBankRequestInfo') }}</span>
                                </template>
                            </v-checkbox>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card elevation="0" class="px-5 my-4">
                <v-card-text>
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h6 font-weight-bold mb-0 mr-2">{{ $t('finance.loanPrograms.loanDetails') }}</h2>
                        <v-tooltip top color="primary" max-width="350">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon style="color:#0EBF67" v-bind="attrs" v-on="on">
                                    mdi-alert-circle
                                </v-icon>
                            </template>
                            <span>{{ $t('finance.loanPrograms.loanDetailsHint') }}</span>
                        </v-tooltip>
                    </div>
                    <v-row>
                        <v-col cols="12" md="3">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.currency') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-select v-model="formData.currency" :items="currencyOptions" item-text="name"
                                item-value="value" outlined dense :placeholder="$t('finance.loanPrograms.selectCurrency')"
                                :rules="[requiredRule]"></v-select>
                        </v-col>

                        <v-col cols="12" md="3">
                            <div class="d-flex align-center font-weight-medium justify-space-between">
                                <label class=" mb-2 d-block">
                                    {{ $t('finance.loanPrograms.loanAmountMin') }}   
                                    <span class="red--text"> *</span>
                                </label>
                                <label class="">
                                    {{ getSelectedCurrencyCode(formData?.currency) }}
                                </label>
                            </div>
                            <v-text-field v-model="formData.minLoanAmount" type="number" outlined dense
                                :placeholder="$t('finance.loanPrograms.enterMinLoanAmount')"
                                :rules="[requiredRule]"></v-text-field>
                        </v-col>

                        <v-col cols="12" md="3">
                            <div class="d-flex align-center font-weight-medium justify-space-between">
                                <label class="font-weight-medium mb-2 d-block">
                                    {{ $t('finance.loanPrograms.loanAmountMax') }}
                                    <span class="red--text"> *</span>
                                </label>
                                <label class="">
                                    {{ getSelectedCurrencyCode(formData?.currency) }}
                                </label>
                            </div>
                            <v-text-field v-model="formData.maxLoanAmount" type="number" outlined dense
                                :placeholder="$t('finance.loanPrograms.enterMaxLoanAmount')"
                                :rules="[requiredRule]"></v-text-field>
                        </v-col>

                        <v-col cols="12" md="3">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.term') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-row no-gutters class="d-flex unit-combined">
                                <v-col class="unit-input-col">
                                <v-text-field v-model="formData.loanTerm" type="number" dense hide-details outlined class="rounded-r-0"
                                    @input="clearLoanTermError"
                                    :placeholder="$t('finance.loanPrograms.enterHere')"></v-text-field>
                                </v-col>
                                <v-col class="unit-select-col flex-grow-0">
                                <v-select v-model="formData.loanTermUnit" :items="gracePeriodUnitOptions"
                                    item-text="label" item-value="value" dense hide-details outlined @change="clearLoanTermError"
                                    class="unit-select rounded-l-0"></v-select>
                                </v-col>
                            </v-row>
                            <div v-if="loanTermError" class="error--text caption mt-1">{{ loanTermError }}</div>
                        </v-col>

                        <v-col cols="12" md="3" v-if="isBankApprovalSelected">
                            <div class="d-flex align-center font-weight-medium justify-space-between">
                                <label class="font-weight-medium mb-2 d-block">
                                    {{ $t('finance.loanPrograms.interestRate') }}
                                    <span class="red--text"> *</span>
                                </label>
                                <label>%</label>
                            </div>
                            <v-text-field
                                v-model="formData.interestRate"
                                type="number"
                                step="0.1"
                                outlined
                                dense
                                :placeholder="$t('finance.loanPrograms.enterHere')"
                                :rules="[requiredRule]"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3" v-else>
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.yieldPerYear') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-text-field v-model="formData.yield" type="number" step="0.1" outlined dense
                                :placeholder="$t('finance.loanPrograms.enterYieldPerYear')"
                                :rules="[requiredRule]"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.typeOfFinancing') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-combobox v-model="formData.typeOfFinancing" :items="typeOfFinancingOptions" outlined dense
                                :placeholder="$t('finance.loanPrograms.selectTypeOfFinancing')"
                                :rules="[requiredRule]"></v-combobox>
                        </v-col>
                        <v-col cols="12" md="3">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.gracePeriodMax') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-row no-gutters class="d-flex unit-combined">
                                <v-col class="unit-input-col">
                                <v-text-field v-model="formData.gracePeriod" type="number" dense hide-details outlined class="rounded-r-0"
                                    @input="clearGracePeriodError"
                                    :placeholder="$t('finance.loanPrograms.enterHere')"></v-text-field>
                                </v-col>
                                <v-col class="unit-select-col flex-grow-0">
                                <v-select v-model="formData.gracePeriodUnit" :items="gracePeriodUnitOptions"
                                    item-text="label" item-value="value" dense hide-details outlined @change="clearGracePeriodError"
                                    class="unit-select rounded-l-0"></v-select>
                                </v-col>
                            </v-row>
                            <div v-if="gracePeriodError" class="error--text caption mt-1">{{ gracePeriodError }}</div>
                        </v-col>
                        <v-col cols="12" md="3">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.crops') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-combobox v-model="formData.crops" :items="cropsOptions" multiple chips small-chips
                                outlined dense :placeholder="$t('finance.loanPrograms.selectCrops')"
                                :rules="[requiredArrayRule]"></v-combobox>
                        </v-col>
                        <v-col cols="12" md="3">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.country') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-select v-model="formData.country" :items="countries" item-text="name"
                                item-value="code" return-object outlined dense @change="getRegion" :placeholder="$t('finance.loanPrograms.selectCountry')"
                                :rules="[requiredRule]"></v-select>
                        </v-col>
                        <v-col cols="12" md="3">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.regions') }}
                                <span class="red--text"> *</span>
                            </label>
                            <v-select v-model="formData.regions" :items="regions" item-text="name" item-value="name" outlined dense
                                :placeholder="$t('finance.loanPrograms.selectRegions')"
                                :rules="[requiredRule]"></v-select>
                        </v-col>

                    </v-row>

                    <hr class="my-4">

                    <v-row>
                        <!-- Display added data fields -->
                        <v-col cols="12" md="12" class="data-fields-column">
                            <div v-if="dataFields.length > 0" class="mb-4">
                                <v-row class="data-fields-grid">
                                <v-col
                                    v-for="(field, index) in dataFields"
                                    :key="index"
                                    cols="12"
                                    :md="field.isGroup ? 12 : 4"
                                    class="data-field-grid-col"
                                >
                                <div
                                    :class="['data-field-item', 'mb-4', { 'group-data-field-item': field.isGroup }]"
                                >
                                    <div class="d-flex align-center justify-space-between mb-2">
                                        <div class="d-flex align-center">

                                            <h4 class="data-field-label font-weight-bold mr-2">
                                                {{ field.isGroup ? field.groupTitle : field.fieldTitle }}
                                                <span v-if="isProgramFieldMandatory(field)" class="red--text"> *</span>
                                            </h4>
                                            
                                            <v-tooltip v-if="field.helpText" top color="primary" max-width="350">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon style="color:#0EBF67" v-bind="attrs" v-on="on">
                                                        mdi-alert-circle
                                                    </v-icon>
                                                </template>
                                                <span>{{ field.helpText }}</span>
                                            </v-tooltip>
                                        </div>

                                        <!-- if group only show group acton button  -->
                                        <template v-if="field.isGroup">
                                            <div class="d-flex justify-end mb-3">
                                                <v-btn color="white" class="flex-shrink-0 mr-2"
                                                    style="background-color: #0EBF67;" icon text
                                                    @click="editDataField(index)" title="Edit field">
                                                    <v-icon>mdi-pencil</v-icon>
                                                </v-btn>
                                                <v-btn color="white" class="flex-shrink-0"
                                                    style="background-color: #DA3D3D;" icon text
                                                    @click="removeDataField(index)" title="Remove field">
                                                    <v-icon>mdi-close</v-icon>
                                                </v-btn>
                                            </div>
                                         </template>
                                    </div>

                                    <!-- Group: show all subfields with inputs -->
                                    <template v-if="field.isGroup">
                                        <div class="group-fields-container pa-4 rounded">
                                            <v-row>
                                                <!-- Loop through each subfield in the group -->
                                                <v-col v-for="(subField, subIndex) in field.groupFields" :key="subIndex" cols="12" sm="6" md="3" class="group-subfield-col">
                                                    <!-- Subfield Label -->
                                                    <div class="d-flex align-center mb-2">
                                                        <label class="font-weight-bold mr-2 group-subfield-label">
                                                            {{ subField.fieldTitle }}
                                                            <span v-if="isProgramFieldMandatory(subField)" class="red--text"> *</span>
                                                        </label>
                                                        <v-tooltip v-if="subField.helpText" top color="primary" max-width="350">
                                                            <template v-slot:activator="{ on, attrs }">
                                                                <v-icon small style="color:#0EBF67" v-bind="attrs" v-on="on">
                                                                    mdi-alert-circle
                                                                </v-icon>
                                                            </template>
                                                            <span>{{ subField.helpText }}</span>
                                                        </v-tooltip>
                                                    </div>

                                                    <!-- Subfield Input based on dataType -->
                                                    <div :class="['subfield-input', { 'subfield-input--transparent': ['radio_button', 'check_boxes'].includes(subField.dataType) }]">
                                                        <!-- Date -->
                                                        <template v-if="subField.dataType === 'date_field'">
                                                            <v-menu v-model="subField._dateMenu" :close-on-content-click="false" offset-y min-width="auto">
                                                                <template v-slot:activator="{ on, attrs }">
                                                                    <v-text-field
                                                                        :value="formatDateDisplay(subField.value)"
                                                                        readonly outlined dense
                                                                        :rules="programDetailValidationRules(subField)"
                                                                        :placeholder="$t('Select date')"
                                                                        v-bind="attrs" v-on="on"
                                                                        append-icon="mdi-calendar"
                                                                    ></v-text-field>
                                                                </template>
                                                                <v-date-picker v-model="subField.value" @input="subField._dateMenu = false" no-title scrollable></v-date-picker>
                                                            </v-menu>
                                                        </template>
                                                        <!-- Text Field -->
                                                        <template v-else-if="subField.dataType === 'text_field'">
                                                            <v-text-field v-model="subField.value" outlined dense :rules="programDetailValidationRules(subField)" :placeholder="$t('Enter value')"></v-text-field>
                                                        </template>
                                                        <!-- Text Area -->
                                                        <template v-else-if="subField.dataType === 'text_area'">
                                                            <v-textarea v-model="subField.value" outlined dense rows="3" :rules="programDetailValidationRules(subField)" :placeholder="$t('Enter value')"></v-textarea>
                                                        </template>
                                                        <!-- Numeric -->
                                                        <template v-else-if="subField.dataType === 'text_field_numeric'">
                                                            <v-text-field v-model="subField.value" type="number" outlined dense :rules="programDetailValidationRules(subField)" :placeholder="$t('Enter number')"></v-text-field>
                                                        </template>
                                                        <!-- Dropdown -->
                                                        <template v-else-if="subField.dataType === 'dropdown'">
                                                            <v-select v-model="subField.value" :items="subField.options || []" outlined dense :rules="programDetailValidationRules(subField)" :placeholder="$t('Select option')"></v-select>
                                                        </template>
                                                        <!-- Radio Button -->
                                                        <template v-else-if="subField.dataType === 'radio_button'">
                                                            <v-radio-group v-model="subField.value" :rules="programDetailValidationRules(subField)" class="mt-0 pt-0 group-options-vertical" color="primary">
                                                                <v-radio
                                                                    v-for="(option, optIndex) in subField.options || []"
                                                                    :key="optIndex"
                                                                    :label="radioOptionLabel(option)"
                                                                    :value="radioOptionValue(option)"
                                                                ></v-radio>
                                                            </v-radio-group>
                                                        </template>
                                                        <!-- Check Boxes -->
                                                        <template v-else-if="subField.dataType === 'check_boxes'">
                                                            <v-select
                                                                v-model="subField.value"
                                                                :items="subField.options || []"
                                                                multiple
                                                                chips
                                                                small-chips
                                                                outlined
                                                                dense
                                                                :rules="programDetailValidationRules(subField)"
                                                                :placeholder="$t('Select options')"
                                                            ></v-select>
                                                        </template>
                                                        <!-- Measurement Unit -->
                                                        <template v-else-if="subField.dataType === 'measurement_unit'">
                                                            <v-row no-gutters class="d-flex unit-combined">
                                                                <v-col class="unit-input-col">
                                                                <v-text-field v-model="subField.value" type="number" dense hide-details outlined class="rounded-r-0"
                                                                    :rules="programDetailValidationRules(subField)"
                                                                    :placeholder="subField.unitType || $t('Value')"
                                                                    ></v-text-field>
                                                                </v-col>
                                                                <v-col class="unit-select-col flex-grow-0">
                                                                <v-select v-model="subField.measurementType"
                                                                    :items="getMeasurementUnits(subField)" dense hide-details outlined
                                                                    class="unit-select rounded-l-0"></v-select>
                                                                </v-col>
                                                            </v-row>
                                                        </template>
                                                        <!-- Attach File -->
                                                        <template v-else-if="subField.dataType === 'attach_file'">
                                                            <div class="attach-file-upload">
                                                                <div
                                                                    class="attach-file-upload__dropzone"
                                                                    :class="{
                                                                        'attach-file-upload__dropzone--drag': attachDragKey === attachKeyGroup(index, subIndex),
                                                                        'attach-file-upload__dropzone--error': !!subField._programDetailError,
                                                                    }"
                                                                    role="button"
                                                                    tabindex="0"
                                                                    :aria-label="$t('Select file to upload')"
                                                                    @click="triggerGroupFileInput(index, subIndex)"
                                                                    @keydown.enter.prevent="triggerGroupFileInput(index, subIndex)"
                                                                    @keydown.space.prevent="triggerGroupFileInput(index, subIndex)"
                                                                    @dragenter.prevent="attachDragKey = attachKeyGroup(index, subIndex)"
                                                                    @dragover.prevent="attachDragKey = attachKeyGroup(index, subIndex)"
                                                                    @dragleave.prevent="onAttachDragLeave($event, attachKeyGroup(index, subIndex))"
                                                                    @drop.prevent="onAttachDropGroup(index, subIndex, $event)"
                                                                >
                                                                    <v-icon class="attach-file-upload__icon" color="primary">mdi-tray-arrow-up</v-icon>
                                                                    <div class="attach-file-upload__copy">
                                                                        <span class="attach-file-upload__cta">{{ $t('Select file to upload') }}</span>
                                                                        <p class="attach-file-upload__hint">{{ $t('finance.loanPrograms.orDragDrop') }}</p>
                                                                    </div>
                                                                </div>
                                                                <input
                                                                    :ref="`group-file-${index}-${subIndex}`"
                                                                    type="file"
                                                                    class="attach-file-upload__input"
                                                                    :accept="getFileAccept(subField)"
                                                                    @change="(e) => onGroupFileChange(index, subIndex, e)"
                                                                />
                                                                <div v-if="attachFileHasValue(subField)" class="attach-file-upload__preview">
                                                                    <div class="attach-file-upload__tile">
                                                                        <div class="attach-file-upload__toolbar">
                                                                            <button
                                                                                type="button"
                                                                                class="attach-file-upload__icon-btn"
                                                                                :title="$t('Download')"
                                                                                :aria-label="$t('Download')"
                                                                                @click.stop="downloadAttachFile(subField)"
                                                                            >
                                                                                <v-icon small class="attach-file-upload__toolbar-icon">mdi-download</v-icon>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                class="attach-file-upload__icon-btn"
                                                                                :title="$t('finance.loanPrograms.removeFile')"
                                                                                :aria-label="$t('finance.loanPrograms.removeFile')"
                                                                                @click.stop="removeAttachFile(subField, attachKeyGroup(index, subIndex), 'group', index, subIndex)"
                                                                            >
                                                                                <v-icon small class="attach-file-upload__toolbar-icon">mdi-delete</v-icon>
                                                                            </button>
                                                                        </div>
                                                                        <div class="attach-file-upload__tile-body">
                                                                            <img
                                                                                v-if="attachFileThumbUrl(subField, attachKeyGroup(index, subIndex))"
                                                                                :src="attachFileThumbUrl(subField, attachKeyGroup(index, subIndex))"
                                                                                alt=""
                                                                                class="attach-file-upload__thumb"
                                                                            />
                                                                            <span class="attach-file-upload__ext">{{ attachFileExtensionLabel(subField) }}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div class="attach-file-upload__filename" :title="attachFileDisplayName(subField)">
                                                                        {{ attachFileTruncatedName(subField) }}
                                                                    </div>
                                                                </div>
                                                                <div v-if="subField._programDetailError" class="error--text caption mt-1">{{ subField._programDetailError }}</div>
                                                            </div>
                                                        </template>
                                                        <!-- Digital Signature -->
                                                        <template v-else-if="subField.dataType === 'digital_signature'">
                                                            <div>
                                                                <DigitalSignatureField :field="subField" />
                                                                <div v-if="subField._programDetailError" class="error--text caption mt-1">{{ subField._programDetailError }}</div>
                                                            </div>
                                                        </template>
                                                        <!-- Data List -->
                                                        <template v-else-if="subField.dataType === 'data_list'">
                                                            <v-text-field v-model="subField.value" outlined dense :rules="programDetailValidationRules(subField)"
                                                                :placeholder="subField.dynamicDataList ? $t('Select from list') : $t('Enter value')"></v-text-field>
                                                        </template>
                                                        <!-- Fallback -->
                                                        <template v-else>
                                                            <v-text-field v-model="subField.value" outlined dense :rules="programDetailValidationRules(subField)" :placeholder="$t('Enter value')"></v-text-field>
                                                        </template>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </template>

                                    <!-- Single field: input + delete -->
                                    <template v-else>
                                        <div class="data-field-inline-row">
                                            <div class="data-field-input-wrap">
                                                <!-- Date -->
                                                <template v-if="field.dataType === 'date_field'">
                                                    <v-menu v-model="field._dateMenu" :close-on-content-click="false"
                                                        offset-y min-width="auto">
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-text-field :value="formatDateDisplay(field.value)"
                                                                readonly outlined dense
                                                                :rules="programDetailValidationRules(field)"
                                                                :placeholder="$t('Select date')" v-bind="attrs"
                                                                v-on="on" append-icon="mdi-calendar"></v-text-field>
                                                        </template>
                                                        <v-date-picker v-model="field.value"
                                                            @input="field._dateMenu = false" no-title
                                                            scrollable></v-date-picker>
                                                    </v-menu>
                                                </template>
                                                <!-- Text -->
                                                <template v-else-if="field.dataType === 'text_field'">
                                                    <v-text-field v-model="field.value" outlined dense :rules="programDetailValidationRules(field)"
                                                        :placeholder="$t('Enter value')"></v-text-field>
                                                </template>
                                                <!-- Text Area -->
                                                <template v-else-if="field.dataType === 'text_area'">
                                                    <v-textarea v-model="field.value" outlined dense rows="3" :rules="programDetailValidationRules(field)"
                                                        :placeholder="$t('Enter value')"></v-textarea>
                                                </template>
                                                <!-- Numeric -->
                                                <template v-else-if="field.dataType === 'text_field_numeric'">
                                                    <v-text-field v-model="field.value" type="number" outlined dense
                                                        :rules="programDetailValidationRules(field)" :placeholder="$t('Enter number')"></v-text-field>
                                                </template>
                                                <!-- Dropdown -->
                                                <template v-else-if="field.dataType === 'dropdown'">
                                                    <v-select v-model="field.value" :items="field.options || []"
                                                        outlined dense :rules="programDetailValidationRules(field)"
                                                        :placeholder="$t('Select option')"></v-select>
                                                </template>
                                                <!-- Radio: real radio controls (same as group subfields; not v-select) -->
                                                <template v-else-if="field.dataType === 'radio_button'">
                                                    <v-radio-group
                                                        v-model="field.value"
                                                        :rules="programDetailValidationRules(field)"
                                                        class="mt-0 pt-0 group-options-vertical"
                                                        color="primary"
                                                    >
                                                        <v-radio
                                                            v-for="(option, optIndex) in field.options || []"
                                                            :key="optIndex"
                                                            :label="radioOptionLabel(option)"
                                                            :value="radioOptionValue(option)"
                                                        ></v-radio>
                                                    </v-radio-group>
                                                </template>
                                                <!-- Check boxes: show comma-separated or select multiple -->
                                                <template v-else-if="field.dataType === 'check_boxes'">
                                                    <v-select v-model="field.value" :items="field.options || []"
                                                        multiple chips small-chips outlined dense :rules="programDetailValidationRules(field)"
                                                        :placeholder="$t('Select options')"></v-select>
                                                </template>
                                                <!-- Measurement Unit -->
                                                <template v-else-if="field.dataType === 'measurement_unit'">
                                                    <v-row no-gutters class="d-flex unit-combined">
                                                        <v-col class="unit-input-col">
                                                        <v-text-field v-model="field.value" type="number" dense hide-details outlined class="rounded-r-0"
                                                            :rules="programDetailValidationRules(field)"
                                                            :placeholder="field.unitType || $t('Value')"
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col class="unit-select-col flex-grow-0">
                                                        <v-select v-model="field.measurementType"
                                                            :items="getMeasurementUnits(field)" dense hide-details outlined
                                                            class="unit-select rounded-l-0"></v-select>
                                                        </v-col>
                                                    </v-row>
                                                </template>
                                                <!-- Attach File -->
                                                <template v-else-if="field.dataType === 'attach_file'">
                                                    <div class="attach-file-upload">
                                                        <div
                                                            class="attach-file-upload__dropzone"
                                                            :class="{
                                                                'attach-file-upload__dropzone--drag': attachDragKey === attachKeySingle(index),
                                                                'attach-file-upload__dropzone--error': !!field._programDetailError,
                                                            }"
                                                            role="button"
                                                            tabindex="0"
                                                            :aria-label="$t('Select file to upload')"
                                                            @click="triggerFileInput(index)"
                                                            @keydown.enter.prevent="triggerFileInput(index)"
                                                            @keydown.space.prevent="triggerFileInput(index)"
                                                            @dragenter.prevent="attachDragKey = attachKeySingle(index)"
                                                            @dragover.prevent="attachDragKey = attachKeySingle(index)"
                                                            @dragleave.prevent="onAttachDragLeave($event, attachKeySingle(index))"
                                                            @drop.prevent="onAttachDropSingle(index, $event)"
                                                        >
                                                            <v-icon class="attach-file-upload__icon" color="primary">mdi-tray-arrow-up</v-icon>
                                                            <div class="attach-file-upload__copy">
                                                                <span class="attach-file-upload__cta">{{ $t('Select file to upload') }}</span>
                                                                <p class="attach-file-upload__hint">{{ $t('finance.loanPrograms.orDragDrop') }}</p>
                                                            </div>
                                                        </div>
                                                        <input
                                                            :ref="`file-${index}`"
                                                            type="file"
                                                            class="attach-file-upload__input"
                                                            :accept="getFileAccept(field)"
                                                            @change="(e) => onFileChange(index, e)"
                                                        />
                                                        <div v-if="attachFileHasValue(field)" class="attach-file-upload__preview">
                                                            <div class="attach-file-upload__tile">
                                                                <div class="attach-file-upload__toolbar">
                                                                    <button
                                                                        type="button"
                                                                        class="attach-file-upload__icon-btn"
                                                                        :title="$t('Download')"
                                                                        :aria-label="$t('Download')"
                                                                        @click.stop="downloadAttachFile(field)"
                                                                    >
                                                                        <v-icon small class="attach-file-upload__toolbar-icon">mdi-download</v-icon>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        class="attach-file-upload__icon-btn"
                                                                        :title="$t('finance.loanPrograms.removeFile')"
                                                                        :aria-label="$t('finance.loanPrograms.removeFile')"
                                                                        @click.stop="removeAttachFile(field, attachKeySingle(index), 'single', index)"
                                                                    >
                                                                        <v-icon small class="attach-file-upload__toolbar-icon">mdi-delete</v-icon>
                                                                    </button>
                                                                </div>
                                                                <div class="attach-file-upload__tile-body">
                                                                    <img
                                                                        v-if="attachFileThumbUrl(field, attachKeySingle(index))"
                                                                        :src="attachFileThumbUrl(field, attachKeySingle(index))"
                                                                        alt=""
                                                                        class="attach-file-upload__thumb"
                                                                    />
                                                                    <span class="attach-file-upload__ext">{{ attachFileExtensionLabel(field) }}</span>
                                                                </div>
                                                            </div>
                                                            <div class="attach-file-upload__filename" :title="attachFileDisplayName(field)">
                                                                {{ attachFileTruncatedName(field) }}
                                                            </div>
                                                        </div>
                                                        <div v-if="field._programDetailError" class="error--text caption mt-1">{{ field._programDetailError }}</div>
                                                    </div>
                                                </template>
                                                <!-- Digital Signature -->
                                                <template v-else-if="field.dataType === 'digital_signature'">
                                                    <div>
                                                        <DigitalSignatureField :field="field" />
                                                        <div v-if="field._programDetailError" class="error--text caption mt-1">{{ field._programDetailError }}</div>
                                                    </div>
                                                </template>
                                                <!-- Data List -->
                                                <template v-else-if="field.dataType === 'data_list'">
                                                    <v-text-field v-model="field.value" outlined dense :rules="programDetailValidationRules(field)"
                                                        :placeholder="field.dynamicDataList ? $t('Select from list') : $t('Enter value')"></v-text-field>
                                                </template>
                                                <!-- Fallback -->
                                                <template v-else>
                                                    <v-text-field v-model="field.value" outlined dense :rules="programDetailValidationRules(field)"
                                                        :placeholder="$t('Enter value')"></v-text-field>
                                                </template>
                                            </div>
                                            <div class="data-field-inline-actions">
                                                <v-btn color="white" class="data-field-action-btn mr-2"
                                                style="background-color: #616161;" icon text
                                                @click="editDataField(index)" title="Edit field">
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn>
                                            <v-btn icon color="white" class="data-field-action-btn"
                                                style="background-color: #DA3D3D;" @click="removeDataField(index)"
                                                title="Remove">
                                                <v-icon>mdi-close</v-icon>
                                            </v-btn>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                                </v-col>
                                </v-row>
                            </div>
                        </v-col>
                    </v-row>
                    <v-col cols="12">
                        <div class="d-flex align-center">
                            <v-btn color="primary" @click="addDataField">
                                <v-icon small class="mr-2">mdi-database-plus</v-icon>
                                {{ $t('finance.loanPrograms.addNewDataField') }}
                            </v-btn>
                        </div>
                    </v-col>
                    <v-col cols="12">
                        <label class="font-weight-medium mb-2 d-block">
                            {{ $t('finance.loanPrograms.description') }}
                        </label>
                        <v-textarea v-model="formData.description" outlined dense rows="4" auto-grow
                            :placeholder="$t('finance.loanPrograms.enterProgramDescription')"></v-textarea>
                    </v-col>
                    <v-col cols="12">
                        <label class="font-weight-medium mb-2 d-block">
                            {{ $t('finance.loanPrograms.conditionsRequirements') }}
                        </label>
                        <quill-editor v-model="formData.conditionsRequirements" :options="editorOptions"
                            @blur="emitContent" class="my-quill" />
                    </v-col>
                </v-card-text>
            </v-card>
        </v-form>
    </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor';
import { getCountries, getStates } from 'country-state-picker'
import YieldPredictionService from '@/_services/YieldPredictionService';
import DigitalSignatureField from './DigitalSignatureField.vue';
import {
    BACKEND_FILE_FIELD_TYPES,
    normalizeFieldType,
    getChoiceOptions,
    parseNumericFieldValue,
    hasMandatoryDynamicFileProvided,
} from '@/utils/loanProgramFieldPayload';

export default {
    name: 'StepOne',
    components: { "quill-editor": quillEditor, DigitalSignatureField },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        formData: {
            type: Object,
            required: true,
        },
        existingLogoUrl: {
            type: String,
            default: null,
        },
        dataFields: {
            type: Array,
            default: () => [],
        },
        bankOptions: {
            type: Array,
            default: () => [],
        },
        currencyOptions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            countries: getCountries(),
            regions: [],
            step1Valid: false,
            logoDragOver: false,
            logoPreviewBlobUrl: null,
            programLogoCleared: false,
            programLogoError: '',
            loanTermError: '',
            gracePeriodError: '',
            typeOfFinancingOptions: [
                'Asset Financing',
                'Personal Financing',
                'Mortgage Financing',
                'Plot Financing',
                'Construction Financing',
                'Stock Financing',
                'Trade Financing',
                'LPO Financing',
                'Working Capital Financing',
                'Auto Financing',
            ],
            cropsOptions: ['Coffee', 'Cocoa', 'Rice', 'Maize (corn)', 'Wheat', 'Soybean'],
            gracePeriodUnitOptions: [
                { label: this.$t('Months'), value: 'month' },
                { label: this.$t('Year'), value: 'year' },
            ],
            editorOptions: {
                theme: "snow",
                placeholder: "Start typing...",
                modules: {
                    toolbar: [
                        [{ font: [] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ color: [] }, { background: [] }],
                        [{ align: [] }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link", "image"],
                        ["clean"]
                    ]
                }
            },
            signaturePadState: {
                isDrawing: false,
                canvasRef: null,
                fieldRef: null,
                lastX: 0,
                lastY: 0,
            },
            attachDragKey: null,
            attachBlobUrlByKey: {},
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(v) {
                this.step1Valid = v;
            },
        },
        step1Valid(v) {
            this.$emit('input', v);
        },
        existingLogoUrl(url) {
            if (url && !(this.formData.programImage instanceof File)) {
                this.programLogoCleared = false;
            }
        },
        'formData.country': {
            immediate: true,
            handler(countryVal) {
                this.syncRegionsForCountry(countryVal, { resetSelectedRegion: false });
            },
        },
    },
    computed: {
        isBankApprovalSelected() {
            return String(this.formData.applicationApproval || '').toUpperCase() === 'BANK';
        },
        logoDisplayUrl() {
            if (this.formData.programImage instanceof File && this.logoPreviewBlobUrl) {
                return this.logoPreviewBlobUrl;
            }
            if (!this.programLogoCleared && this.existingLogoUrl && !(this.formData.programImage instanceof File)) {
                return this.existingLogoUrl;
            }
            return null;
        },
        logoShowPreviewTile() {
            return !!this.logoDisplayUrl;
        },
        logoExtensionLabel() {
            const file = this.formData.programImage;
            if (file instanceof File && file.name) {
                const i = file.name.lastIndexOf('.');
                if (i > -1 && i < file.name.length - 1) {
                    return file.name.slice(i + 1).toUpperCase().slice(0, 5);
                }
            }
            if (this.existingLogoUrl && typeof this.existingLogoUrl === 'string') {
                const seg = this.existingLogoUrl.split('/').pop() || '';
                const clean = seg.split('?')[0];
                const j = clean.lastIndexOf('.');
                if (j > -1 && j < clean.length - 1) {
                    return clean.slice(j + 1).toUpperCase().slice(0, 5);
                }
            }
            return 'IMG';
        },
        logoFileTitle() {
            const file = this.formData.programImage;
            if (file instanceof File) return file.name || '';
            if (this.existingLogoUrl && !this.programLogoCleared) {
                const seg = this.existingLogoUrl.split('/').pop() || '';
                try {
                    return decodeURIComponent(seg.split('?')[0]) || this.existingLogoUrl;
                } catch {
                    return seg.split('?')[0] || this.existingLogoUrl;
                }
            }
            return '';
        },
        logoTruncatedFileName() {
            const max = 22;
            const raw =
                this.formData.programImage instanceof File
                    ? this.formData.programImage.name
                    : this.logoFileTitle || this.$t('Program logo');
            if (!raw) return '';
            if (raw.length <= max) return raw;
            return `${raw.slice(0, max - 3)}...`;
        },
    },
    mounted() {
        if (this.formData.programImage instanceof File) {
            this.logoPreviewBlobUrl = URL.createObjectURL(this.formData.programImage);
        }
        this.getCrops();
    },
    beforeDestroy() {
        this.revokeLogoPreviewBlob();
        this.revokeAllAttachBlobs();
    },
    methods: {
        requiredRule(v) {
            if (Array.isArray(v)) return v.length > 0 || this.$t('finance.loanPrograms.fieldRequired');
            if (v === undefined || v === null) return this.$t('finance.loanPrograms.fieldRequired');
            return String(v).trim() !== '' || this.$t('finance.loanPrograms.fieldRequired');
        },
        requiredArrayRule(v) {
            return Array.isArray(v) && v.length > 0 || this.$t('finance.loanPrograms.fieldRequired');
        },
        hasProgramLogo() {
            if (this.formData.programImage instanceof File) return true;
            if (this.existingLogoUrl && !this.programLogoCleared) return true;
            return false;
        },
        isQuillContentEmpty(value) {
            if (value === undefined || value === null) return true;
            const plain = String(value)
                .replace(/<(.|\n)*?>/g, '')
                .replace(/&nbsp;/g, ' ')
                .trim();
            return plain === '';
        },
        validateCustomRequiredFields() {
            this.programLogoError = this.hasProgramLogo() ? '' : this.$t('finance.loanPrograms.fieldRequired');
            const termValue = this.formData.loanTerm;
            const hasTermValue = !(termValue === undefined || termValue === null || String(termValue).trim() === '');
            const hasTermUnit = !!this.formData.loanTermUnit;
            this.loanTermError = hasTermValue && hasTermUnit ? '' : this.$t('finance.loanPrograms.fieldRequired');

            const graceValue = this.formData.gracePeriod;
            const hasGraceValue = !(graceValue === undefined || graceValue === null || String(graceValue).trim() === '');
            const hasGraceUnit = !!this.formData.gracePeriodUnit;
            this.gracePeriodError = hasGraceValue && hasGraceUnit ? '' : this.$t('finance.loanPrograms.fieldRequired');
            return !this.programLogoError && !this.loanTermError && !this.gracePeriodError;
        },
        clearLoanTermError() {
            this.loanTermError = '';
        },
        clearGracePeriodError() {
            this.gracePeriodError = '';
        },
        getDefaultCropOptions() {
            return ['Coffee', 'Cocoa', 'Rice', 'Maize (corn)', 'Wheat', 'Soybean'];
        },
        async getCrops() {
            try {
                const res = await YieldPredictionService.getCropList();
                const crops = res?.data?.crops;
                if (!Array.isArray(crops) || !crops.length) {
                    this.cropsOptions = this.getDefaultCropOptions();
                    return;
                }

                const normalizedCrops = crops
                    .map((crop) => {
                        if (typeof crop === 'string') return crop;
                        if (crop && typeof crop === 'object') {
                            return crop.name || crop.crop || crop.id || '';
                        }
                        return '';
                    })
                    .filter(Boolean);

                this.cropsOptions = normalizedCrops.length
                    ? [...new Set(normalizedCrops)]
                    : this.getDefaultCropOptions();
            } catch (error) {
                console.error('Error fetching crops:', error);
                this.cropsOptions = this.getDefaultCropOptions();
            }
        },
        getSelectedCurrencyCode(selectedCurrency) {
            if (!selectedCurrency) return 'USD';

            if (typeof selectedCurrency === 'string') {
                return selectedCurrency;
            }

            if (typeof selectedCurrency === 'object') {
                if (selectedCurrency.value) return selectedCurrency.value;
                if (selectedCurrency.code) return selectedCurrency.code;
                if (selectedCurrency.name) {
                    const matchedOption = this.currencyOptions.find(
                        option => option.name === selectedCurrency.name
                    );
                    if (matchedOption?.value) return matchedOption.value;
                }
            }

            return 'USD';
        },
        getRegion(val) {
            this.syncRegionsForCountry(val, { resetSelectedRegion: true });
        },
        resolveCountryOption(val) {
            if (!val) return null;
            const rawCode = typeof val === 'object' ? val.code : val;
            const rawName = typeof val === 'object' ? val.name : val;
            const code = rawCode != null ? String(rawCode).trim().toLowerCase() : '';
            const name = rawName != null ? String(rawName).trim().toLowerCase() : '';
            const match = (this.countries || []).find((country) => {
                const countryCode = String(country.code || '').trim().toLowerCase();
                const countryName = String(country.name || '').trim().toLowerCase();
                return (code && countryCode === code) || (name && countryName === name);
            });
            return match || (typeof val === 'object' ? val : null);
        },
        syncRegionsForCountry(val, { resetSelectedRegion = false } = {}) {
            const resolved = this.resolveCountryOption(val);
            const currentCode = String(this.formData.country?.code || '').trim().toLowerCase();
            const resolvedCode = String(resolved?.code || '').trim().toLowerCase();

            if (resolved && currentCode !== resolvedCode) {
                this.$set(this.formData, 'country', resolved);
            }

            this.regions = resolved ? getStates(resolved.code).map(item => ({ name: item })) : [];

            if (!resolved) {
                this.formData.regions = null;
                return;
            }

            if (resetSelectedRegion) {
                this.formData.regions = null;
            }
        },
        /** Normalize option entry for v-radio :value (aligned with getChoiceOptions). */
        radioOptionValue(option) {
            if (option != null && typeof option === 'object' && option.value !== undefined) {
                return option.value;
            }
            return option;
        },
        /** Label text for v-radio (string or { text, label, value }). */
        radioOptionLabel(option) {
            if (option != null && typeof option === 'object') {
                const t = option.text ?? option.label;
                if (t != null && t !== '') return String(t);
                if (option.value !== undefined) return String(option.value);
            }
            return String(option);
        },
        isProgramFieldMandatory(f) {
            return !!(f && (f.isMandetory));
        },
        clearProgramDetailFileErrors() {
            (this.dataFields || []).forEach((field) => {
                if (field.isGroup) {
                    (field.groupFields || []).forEach((sub) => {
                        this.$set(sub, '_programDetailError', '');
                    });
                } else {
                    this.$set(field, '_programDetailError', '');
                }
            });
        },
        clearProgramDetailFieldError(field) {
            if (field) this.$set(field, '_programDetailError', '');
        },
        programDetailValidationRules(field) {
            if (!this.isProgramFieldMandatory(field)) return [];
            const t = normalizeFieldType(field.dataType);
            if (BACKEND_FILE_FIELD_TYPES.includes(t)) return [];
            return [this.buildProgramDetailRule(field, t)];
        },
        buildProgramDetailRule(field, t) {
            return (v) => {
                const mandatory = this.isProgramFieldMandatory(field);
                if (!mandatory) return true;
                const label = field.fieldTitle || '';

                if (t === 'check_boxes') {
                    const arr = Array.isArray(v) ? v : field.value;
                    if (!Array.isArray(arr) || arr.length === 0) {
                        return `${this.$t('finance.loanPrograms.selectAtLeastOneOptionFor')}: ${label}`;
                    }
                    return true;
                }
                if (t === 'dropdown' && field.allowMultipleInputs) {
                    const arr = Array.isArray(v) ? v : field.value;
                    if (!Array.isArray(arr) || arr.length === 0) {
                        return `${this.$t('finance.loanPrograms.selectAtLeastOneOptionFor')}: ${label}`;
                    }
                    return true;
                }
                if (t === 'dropdown' && !field.allowMultipleInputs) {
                    const val = v !== undefined && v !== null ? v : field.value;
                    if (val === undefined || val === null || val === '') {
                        return this.$t('finance.loanPrograms.fieldRequired');
                    }
                    return true;
                }
                if (t === 'radio_button') {
                    const choices = getChoiceOptions(field);
                    const val = v !== undefined && v !== null ? v : field.value;
                    if (val === undefined || val === null || val === '' || !choices.includes(val)) {
                        return `${this.$t('finance.loanPrograms.chooseValidOptionFor')}: ${label}`;
                    }
                    return true;
                }
                if (t === 'measurement_unit') {
                    const numVal = v !== undefined && v !== null ? v : field.value;
                    const n = parseNumericFieldValue(numVal);
                    if (n === null) {
                        return `${this.$t('finance.loanPrograms.enterValidAmountFor')}: ${label}`;
                    }
                    const units = Array.isArray(field.units) && field.units.length
                        ? field.units
                        : (field.measurementType ? [field.measurementType] : []);
                    const unit = field.measurementType || units[0] || '';
                    if (units.length && unit && !units.includes(unit)) {
                        return `${this.$t('finance.loanPrograms.selectValidUnitFor')}: ${label}`;
                    }
                    return true;
                }
                if (t === 'date_field') {
                    if (!field.value) return this.$t('finance.loanPrograms.fieldRequired');
                    return true;
                }
                if (t === 'text_field_numeric') {
                    const val = v !== undefined && v !== null ? v : field.value;
                    if (val === undefined || val === null || val === '') {
                        return this.$t('finance.loanPrograms.fieldRequired');
                    }
                    return true;
                }
                const val = v !== undefined && v !== null ? v : field.value;
                if (val === undefined || val === null || String(val).trim() === '') {
                    return this.$t('finance.loanPrograms.fieldRequired');
                }
                return true;
            };
        },
        validateMandatoryProgramDetailFileFields() {
            let ok = true;
            const walk = (f) => {
                const t = normalizeFieldType(f.dataType);
                if (!BACKEND_FILE_FIELD_TYPES.includes(t)) return;
                if (!this.isProgramFieldMandatory(f)) {
                    this.$set(f, '_programDetailError', '');
                    return;
                }
                if (!hasMandatoryDynamicFileProvided(f)) {
                    const label = f.fieldTitle || '';
                    this.$set(
                        f,
                        '_programDetailError',
                        `${this.$t('finance.loanPrograms.fileUploadRequiredFor')}: ${label}`,
                    );
                    ok = false;
                } else {
                    this.$set(f, '_programDetailError', '');
                }
            };
            (this.dataFields || []).forEach((field) => {
                if (field.isGroup) {
                    (field.groupFields || []).forEach(walk);
                } else {
                    walk(field);
                }
            });
            return ok;
        },
        validate() {
            this.clearProgramDetailFileErrors();
            const customOk = this.validateCustomRequiredFields();
            const formOk = this.$refs.step1Form ? this.$refs.step1Form.validate() : false;
            const filesOk = !formOk || this.validateMandatoryProgramDetailFileFields();
            return customOk && formOk && filesOk;
        },
        revokeLogoPreviewBlob() {
            if (this.logoPreviewBlobUrl) {
                URL.revokeObjectURL(this.logoPreviewBlobUrl);
                this.logoPreviewBlobUrl = null;
            }
        },
        assignProgramLogoFile(file) {
            if (!file || !file.type || !file.type.startsWith('image/')) return;
            this.programLogoCleared = false;
            this.programLogoError = '';
            this.revokeLogoPreviewBlob();
            this.logoPreviewBlobUrl = URL.createObjectURL(file);
            this.formData.programImage = file;
        },
        handleFileSelect(event) {
            const file = event.target.files && event.target.files[0];
            if (file) {
                this.assignProgramLogoFile(file);
            }
            if (event.target) event.target.value = '';
        },
        onLogoDragLeave(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.logoDragOver = false;
            }
        },
        onLogoDrop(event) {
            this.logoDragOver = false;
            const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            if (file) {
                this.assignProgramLogoFile(file);
            }
        },
        triggerProgramLogoInput() {
            const input = this.$refs.fileInput;
            if (input && input.click) input.click();
        },
        removeProgramLogo() {
            this.revokeLogoPreviewBlob();
            this.formData.programImage = null;
            this.programLogoCleared = true;
            this.programLogoError = this.$t('finance.loanPrograms.fieldRequired');
            const input = this.$refs.fileInput;
            if (input) input.value = '';
        },
        downloadProgramLogo() {
            const file = this.formData.programImage;
            if (file instanceof File) {
                const url = URL.createObjectURL(file);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.name || 'program-logo';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                return;
            }
            if (this.existingLogoUrl && !this.programLogoCleared) {
                const a = document.createElement('a');
                a.href = this.existingLogoUrl;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                a.download = '';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        },
        triggerFileInput(index) {
            if (index === undefined || typeof index !== 'number') {
                this.triggerProgramLogoInput();
            } else {
                const ref = this.$refs[`file-${index}`];
                const el = Array.isArray(ref) ? ref[0] : ref;
                if (el && el.click) el.click();
            }
        },
        addDataField() {
            this.$emit('add-data-field');
        },
        editDataField(index) {
            this.$emit('edit-data-field', index);
        },
        removeDataField(index) {
            this.$emit('remove-data-field', index);
        },
        getDataTypeLabel(dataType) {
            const dataTypeMap = {
                text_field: this.$t('Text Field'),
                text_area: this.$t('Text Area'),
                text_field_numeric: this.$t('Text Field Numeric'),
                radio_button: this.$t('Radio Button'),
                check_boxes: this.$t('Check Boxes'),
                dropdown: this.$t('Dropdown'),
                measurement_unit: this.$t('Measurement Unit'),
                attach_file: this.$t('Attach File'),
                digital_signature: this.$t('Digital Signature'),
                data_list: this.$t('Data List'),
                date_field: this.$t('Date'),
                date: this.$t('Date'),
            };
            return dataTypeMap[dataType] || dataType;
        },
        formatDateDisplay(date) {
            if (!date) return '';
            const d = typeof date === 'string' ? new Date(date) : date;
            if (isNaN(d.getTime())) return '';
            const day = String(d.getDate()).padStart(2, '0');
            const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const month = months[d.getMonth()];
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
        },
        getFileAccept(field) {
            if (!field.allowedFileTypes || !field.allowedFileTypes.length) return '*';
            const map = { image: 'image/*', video: 'video/*', docx: '.docx' };
            return (field.allowedFileTypes || []).map(t => map[t] || t).join(',');
        },
        getMeasurementUnits(field) {
            const explicitUnits = Array.isArray(field?.units)
                ? field.units.map((u) => String(u).trim()).filter(Boolean)
                : [];
            const selectedUnit = field?.measurementType != null
                ? String(field.measurementType).trim()
                : '';

            if (explicitUnits.length > 1) {
                if (!selectedUnit || explicitUnits.includes(selectedUnit)) return explicitUnits;
                return [...explicitUnits, selectedUnit];
            }
            // fallback units
            const fallbackUnits = ['cm', 'm', 'km', 'inch', 'foot'];
            return [...new Set([...explicitUnits, selectedUnit, ...fallbackUnits].filter(Boolean))];
        },
        coerceToggle(value, token) {
            if (Array.isArray(value)) return token ? value.includes(token) : value.length > 0;
            if (typeof value === 'string') return value.toLowerCase() === 'true';
            return !!value;
        },
        canUseDigitalSignature(field) {
            const hasDigitalFlag = field?.digitalSignature !== undefined;
            const hasAttachFlag = field?.attachFile !== undefined;
            if (hasDigitalFlag || hasAttachFlag) {
                const allowDigital = hasDigitalFlag ? this.coerceToggle(field.digitalSignature, 'digital-signature') : false;
                const allowAttach = hasAttachFlag ? this.coerceToggle(field.attachFile, 'attach-file') : false;
                return allowDigital || (!allowDigital && !allowAttach);
            }
            return true;
        },
        canUseSignatureFile(field) {
            if (field?.attachFile !== undefined) return this.coerceToggle(field.attachFile, 'attach-file');
            return false;
        },
        getCanvas(refName) {
            const ref = this.$refs[refName];
            return Array.isArray(ref) ? ref[0] : ref;
        },
        getPointer(canvas, event) {
            const rect = canvas.getBoundingClientRect();
            const point = event.touches && event.touches[0] ? event.touches[0] : event;
            return {
                x: point.clientX - rect.left,
                y: point.clientY - rect.top,
            };
        },
        startSignatureDrawing(event, canvasRef, fieldRef) {
            const canvas = this.getCanvas(canvasRef);
            if (!canvas || !fieldRef) return;
            const dpr = window.devicePixelRatio || 1;
            const width = Math.floor(canvas.getBoundingClientRect().width);
            const height = Math.floor(canvas.getBoundingClientRect().height);
            if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
                canvas.width = width * dpr;
                canvas.height = height * dpr;
                const resizeCtx = canvas.getContext('2d');
                resizeCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
            }
            const ctx = canvas.getContext('2d');
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#222';
            const pointer = this.getPointer(canvas, event);
            this.signaturePadState = {
                isDrawing: true,
                canvasRef,
                fieldRef,
                lastX: pointer.x,
                lastY: pointer.y,
            };
            ctx.beginPath();
            ctx.moveTo(pointer.x, pointer.y);
        },
        drawSignature(event) {
            if (!this.signaturePadState.isDrawing || !this.signaturePadState.canvasRef) return;
            const canvas = this.getCanvas(this.signaturePadState.canvasRef);
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            const pointer = this.getPointer(canvas, event);
            ctx.beginPath();
            ctx.moveTo(this.signaturePadState.lastX, this.signaturePadState.lastY);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.stroke();
            this.signaturePadState.lastX = pointer.x;
            this.signaturePadState.lastY = pointer.y;
        },
        endSignatureDrawing() {
            if (!this.signaturePadState.isDrawing) return;
            const { canvasRef, fieldRef } = this.signaturePadState;
            const canvas = this.getCanvas(canvasRef);
            if (canvas && fieldRef) {
                this.$set(fieldRef, 'value', canvas.toDataURL('image/png'));
            }
            this.signaturePadState.isDrawing = false;
            this.signaturePadState.canvasRef = null;
            this.signaturePadState.fieldRef = null;
        },
        clearSignaturePad(canvasRef, fieldRef) {
            const canvas = this.getCanvas(canvasRef);
            if (canvas) {
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            if (fieldRef) {
                this.$set(fieldRef, 'value', '');
            }
        },
        attachKeySingle(index) {
            return `s-${index}`;
        },
        attachKeyGroup(parentIndex, subIndex) {
            return `g-${parentIndex}-${subIndex}`;
        },
        attachFileHasValue(field) {
            const v = field && field.value;
            if (v == null || v === '') return false;
            if (v instanceof File) return true;
            if (typeof v === 'string' && v.trim()) return true;
            return false;
        },
        attachFileDisplayName(field) {
            const v = field && field.value;
            if (v instanceof File) return v.name || '';
            if (typeof v === 'string') {
                const seg = v.split('/').pop() || v;
                try {
                    return decodeURIComponent(seg.split('?')[0]) || v;
                } catch {
                    return seg.split('?')[0] || v;
                }
            }
            return '';
        },
        attachFileTruncatedName(field) {
            const max = 28;
            const raw = this.attachFileDisplayName(field);
            if (!raw) return '';
            if (raw.length <= max) return raw;
            return `${raw.slice(0, max - 3)}...`;
        },
        isAttachImageUrlString(url) {
            if (!url || typeof url !== 'string') return false;
            return /\.(png|jpe?g|gif|webp|svg|bmp)(\?|#|$)/i.test(url);
        },
        attachFileThumbUrl(field, key) {
            const v = field && field.value;
            if (v instanceof File && v.type && v.type.startsWith('image/')) {
                return this.attachBlobUrlByKey[key] || null;
            }
            if (typeof v === 'string' && /^https?:\/\//i.test(v) && this.isAttachImageUrlString(v)) {
                return v;
            }
            return null;
        },
        attachFileExtensionLabel(field) {
            const v = field && field.value;
            const name = this.attachFileDisplayName(field);
            const i = name.lastIndexOf('.');
            if (i > -1 && i < name.length - 1) {
                return name.slice(i + 1).toUpperCase().slice(0, 5);
            }
            if (v instanceof File && v.type) {
                const part = v.type.split('/')[1];
                return (part || 'FILE').toUpperCase().slice(0, 5);
            }
            return 'FILE';
        },
        revokeAttachBlobUrl(key) {
            const u = this.attachBlobUrlByKey[key];
            if (u) {
                URL.revokeObjectURL(u);
                this.$delete(this.attachBlobUrlByKey, key);
            }
        },
        revokeAllAttachBlobs() {
            Object.keys(this.attachBlobUrlByKey || {}).forEach((k) => this.revokeAttachBlobUrl(k));
        },
        refreshAttachBlobUrl(key, file) {
            this.revokeAttachBlobUrl(key);
            if (file instanceof File && file.type && file.type.startsWith('image/')) {
                this.$set(this.attachBlobUrlByKey, key, URL.createObjectURL(file));
            }
        },
        onAttachDragLeave(event, key) {
            if (event.currentTarget && !event.currentTarget.contains(event.relatedTarget)) {
                if (this.attachDragKey === key) this.attachDragKey = null;
            }
        },
        onAttachDropSingle(index, event) {
            this.attachDragKey = null;
            const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            if (!file || !this.dataFields[index]) return;
            this.$set(this.dataFields[index], 'value', file);
            this.refreshAttachBlobUrl(this.attachKeySingle(index), file);
            this.clearProgramDetailFieldError(this.dataFields[index]);
        },
        onAttachDropGroup(parentIndex, subIndex, event) {
            this.attachDragKey = null;
            const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            const subField = this.dataFields[parentIndex] && this.dataFields[parentIndex].groupFields
                ? this.dataFields[parentIndex].groupFields[subIndex]
                : null;
            if (!file || !subField) return;
            this.$set(subField, 'value', file);
            this.refreshAttachBlobUrl(this.attachKeyGroup(parentIndex, subIndex), file);
            this.clearProgramDetailFieldError(subField);
        },
        downloadAttachFile(field) {
            const v = field && field.value;
            if (v instanceof File) {
                const url = URL.createObjectURL(v);
                const a = document.createElement('a');
                a.href = url;
                a.download = v.name || 'download';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                return;
            }
            if (typeof v === 'string' && /^https?:\/\//i.test(v)) {
                window.open(v, '_blank', 'noopener,noreferrer');
            }
        },
        removeAttachFile(field, key, mode, index, subIndex) {
            this.revokeAttachBlobUrl(key);
            this.$set(field, 'value', null);
            this.clearProgramDetailFieldError(field);
            if (mode === 'single' && typeof index === 'number') {
                const ref = this.$refs[`file-${index}`];
                const el = Array.isArray(ref) ? ref[0] : ref;
                if (el) el.value = '';
            }
            if (mode === 'group' && typeof index === 'number' && typeof subIndex === 'number') {
                const ref = this.$refs[`group-file-${index}-${subIndex}`];
                const el = Array.isArray(ref) ? ref[0] : ref;
                if (el) el.value = '';
            }
        },
        onFileChange(index, event) {
            const file = event.target.files && event.target.files[0];
            if (file && this.dataFields[index]) {
                this.$set(this.dataFields[index], 'value', file);
                this.refreshAttachBlobUrl(this.attachKeySingle(index), file);
                this.clearProgramDetailFieldError(this.dataFields[index]);
            }
            if (event.target) event.target.value = '';
        },
        triggerDigitalFileInput(index) {
            const ref = this.$refs[`digital-file-${index}`];
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (el && el.click) el.click();
        },
        onDigitalFileChange(index, event) {
            const file = event.target.files && event.target.files[0];
            if (file && this.dataFields[index]) {
                this.$set(this.dataFields[index], 'signatureFile', file);
                if (!this.canUseDigitalSignature(this.dataFields[index])) {
                    this.$set(this.dataFields[index], 'value', file);
                }
                this.clearProgramDetailFieldError(this.dataFields[index]);
            }
            if (event.target) event.target.value = '';
        },
        triggerGroupFileInput(parentIndex, subIndex) {
            const ref = this.$refs[`group-file-${parentIndex}-${subIndex}`];
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (el && el.click) el.click();
        },
        onGroupFileChange(parentIndex, subIndex, event) {
            const file = event.target.files && event.target.files[0];
            if (file && this.dataFields[parentIndex] && this.dataFields[parentIndex].groupFields[subIndex]) {
                const subField = this.dataFields[parentIndex].groupFields[subIndex];
                this.$set(subField, 'value', file);
                this.refreshAttachBlobUrl(this.attachKeyGroup(parentIndex, subIndex), file);
                this.clearProgramDetailFieldError(subField);
            }
            if (event.target) event.target.value = '';
        },
        triggerDigitalGroupFileInput(parentIndex, subIndex) {
            const ref = this.$refs[`group-digital-file-${parentIndex}-${subIndex}`];
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (el && el.click) el.click();
        },
        onDigitalGroupFileChange(parentIndex, subIndex, event) {
            const file = event.target.files && event.target.files[0];
            const subField = this.dataFields[parentIndex]?.groupFields?.[subIndex];
            if (file && subField) {
                this.$set(subField, 'signatureFile', file);
                if (!this.canUseDigitalSignature(subField)) {
                    this.$set(subField, 'value', file);
                }
                this.clearProgramDetailFieldError(subField);
            }
            if (event.target) event.target.value = '';
        },
        emitContent() {
            this.$emit('conditions-blur', this.formData.conditionsRequirements);
        },
    },
};
</script>

<style scoped>
.data-field-item {
    padding: 12px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.data-field-item:last-child {
    border-bottom: none;
}

.data-fields-column {
    padding-top: 0 !important;
}

.data-fields-grid {
    margin-top: 0;
}

.data-field-grid-col {
    padding-top: 0;
}

.data-field-grid-col .data-field-item {
    height: 100%;
}

.data-field-label {
    font-size: 1rem;
    color: #333;
    line-height: 1.2;
}

/* Row: control + edit/delete — align tops; avoid centering against v-messages slot height */
.data-field-inline-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 8px;
}

.data-field-inline-row .data-field-input-wrap {
    flex: 1 1 auto;
    min-width: 0;
}

.data-field-inline-row .data-field-input-wrap >>> .v-input {
    margin-top: 0 !important;
    padding-top: 0 !important;
}

.data-field-inline-actions {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
}

.data-field-inline-actions .data-field-action-btn {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
}

.group-data-field-item {
    padding: 18px 20px;
    border-radius: 8px;
    border: 1px solid #ececec;
    background-color: transparent;
}

.unit-combined .unit-input-col {
    flex: 1;
    min-width: 60px;
}

.unit-combined .unit-select-col {
    min-width: 145px;
    flex-shrink: 0;
}

.unit-combined .unit-select {
    min-width: 145px;
}

.unit-combined .unit-select >>> .v-input__slot {
    border: none !important;
    background-color: #00bd73 !important;
    border-radius: 0 4px 4px 0 !important;
    padding-left: 12px !important;
    padding-right: 8px !important;
    min-width: 145px !important;
}

.unit-combined .rounded-r-0 >>> .v-input__slot {
    border-radius: 4px 0 0 4px !important;
}

.unit-combined .unit-select >>> .v-select__selection {
    color: #fff !important;
    font-weight: 500 !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: unset !important;
    max-width: none !important;
}

.unit-combined .unit-select >>> .v-input__append-inner .v-icon {
    color: #fff !important;
}

.program-logo-upload {
    position: relative;
    width: 100%;
}

.program-logo-upload__input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.program-logo-upload__dropzone {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    min-height: 96px;
    padding: 20px 22px;
    border: 2px dashed rgba(0, 0, 0, 0.55);
    border-radius: 12px;
    background: #fafafa;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.program-logo-upload__dropzone:hover {
    background: #f3f8ff;
    border-color: rgba(25, 118, 210, 0.55);
}

.program-logo-upload__dropzone:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
}

.program-logo-upload__dropzone--drag {
    background: #e8f4fd;
    border-color: var(--primary-color, #1976d2);
    border-style: solid;
    box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.15);
}

.program-logo-upload__dropzone--error {
    border-color: #d32f2f;
    background: #fff5f5;
}

.program-logo-upload__icon {
    flex-shrink: 0;
    font-size: 48px !important;
}

.program-logo-upload__copy {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.program-logo-upload__cta {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    color: var(--primary-color, #1976d2);
    line-height: 1.3;
}

.program-logo-upload__dropzone:hover .program-logo-upload__cta {
    text-decoration: underline;
    text-underline-offset: 3px;
}

.program-logo-upload__hint {
    margin: 6px 0 0;
    font-size: 0.8125rem;
    color: #9e9e9e;
    line-height: 1.4;
}

.program-logo-upload__preview {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.program-logo-upload__tile {
    position: relative;
    width: 104px;
    height: 104px;
    border-radius: 10px;
    background: #e8f5e9;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(27, 94, 32, 0.12);
}

.program-logo-upload__toolbar {
    position: absolute;
    top: 4px;
    left: 4px;
    right: 4px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    pointer-events: none;
}

.program-logo-upload__icon-btn {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    transition: background 0.15s ease, transform 0.15s ease;
}

.program-logo-upload__icon-btn:hover {
    background: #fff;
    transform: scale(1.05);
}

.program-logo-upload__toolbar-icon {
    color: #1b5e20 !important;
}

.program-logo-upload__tile-body {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.program-logo-upload__thumb {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.35;
}

.program-logo-upload__ext {
    position: relative;
    z-index: 1;
    font-size: 1.35rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #1b5e20;
    text-shadow: 0 0 1px #fff, 0 1px 2px rgba(255, 255, 255, 0.9);
}

.program-logo-upload__filename {
    margin-top: 8px;
    max-width: 120px;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #2e7d32;
    line-height: 1.3;
    word-break: break-all;
}

.editor-wrapper {
    border-radius: 8px;
    background: #fff;
    border: 1.5px solid #e0e0e0;
    display: flex;
    flex-direction: column;
}

/* Group fields container styles */
.group-fields-container {
    border: 1px solid #e0e0e0;
    background-color: transparent;
}

.group-fields-container .subfield-input {
    background-color: transparent;
    border-radius: 4px;
}

.group-fields-container .subfield-input--transparent {
    background-color: transparent !important;
}

.group-subfield-col {
    padding-top: 6px;
    padding-bottom: 6px;
}

.group-subfield-label {
    color: #2f2f2f;
    font-size: 15px;
}

.group-fields-container .v-input {
    margin-top: 0;
}

.group-options-vertical {
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
}

.group-options-vertical .v-input--radio-group__input {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.group-options-vertical .v-radio,
.group-options-vertical .v-input--checkbox {
    margin-top: 0 !important;
    padding-top: 0 !important;
}

.group-option-item {
    margin-right: 0 !important;
}

.group-options-vertical .v-input--selection-controls__input,
.group-options-vertical .v-input--selection-controls__ripple,
.group-options-vertical .v-input--radio-group__input {
    background-color: transparent !important;
}

.signature-wrap {
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fff;
    padding: 8px;
}

.signature-pad {
    width: 100%;
    height: 140px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    background: #fff;
    touch-action: none;
}

@media (max-width: 960px) {
    .group-data-field-item {
        padding: 14px;
    }
}

/* Attach file: same pattern as program logo, compact for data fields */
.attach-file-upload {
    position: relative;
    width: 100%;
}

.attach-file-upload__input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.attach-file-upload__dropzone {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 14px;
    min-height: 72px;
    padding: 14px 16px;
    border: 2px dashed rgba(0, 0, 0, 0.55);
    border-radius: 10px;
    background: #fafafa;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.attach-file-upload__dropzone:hover {
    background: #f3f8ff;
    border-color: rgba(25, 118, 210, 0.55);
}

.attach-file-upload__dropzone:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
}

.attach-file-upload__dropzone--drag {
    background: #e8f4fd;
    border-color: var(--primary-color, #1976d2);
    border-style: solid;
    box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.15);
}

.attach-file-upload__dropzone--error {
    border-color: #b00020 !important;
    border-style: solid;
    background: #fff8f8;
}

.attach-file-upload__icon {
    flex-shrink: 0;
    font-size: 36px !important;
}

.attach-file-upload__copy {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.attach-file-upload__cta {
    display: block;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--primary-color, #1976d2);
    line-height: 1.3;
}

.attach-file-upload__dropzone:hover .attach-file-upload__cta {
    text-decoration: underline;
    text-underline-offset: 3px;
}

.attach-file-upload__hint {
    margin: 4px 0 0;
    font-size: 0.75rem;
    color: #9e9e9e;
    line-height: 1.4;
}

.attach-file-upload__preview {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.attach-file-upload__tile {
    position: relative;
    width: 88px;
    height: 88px;
    border-radius: 8px;
    background: #e8f5e9;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(27, 94, 32, 0.12);
}

.attach-file-upload__toolbar {
    position: absolute;
    top: 3px;
    left: 3px;
    right: 3px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    pointer-events: none;
}

.attach-file-upload__icon-btn {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: none;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    transition: background 0.15s ease, transform 0.15s ease;
}

.attach-file-upload__icon-btn:hover {
    background: #fff;
    transform: scale(1.05);
}

.attach-file-upload__toolbar-icon {
    color: #1b5e20 !important;
}

.attach-file-upload__tile-body {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.attach-file-upload__thumb {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.35;
}

.attach-file-upload__ext {
    position: relative;
    z-index: 1;
    font-size: 1.1rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #1b5e20;
    text-shadow: 0 0 1px #fff, 0 1px 2px rgba(255, 255, 255, 0.9);
}

.attach-file-upload__filename {
    margin-top: 6px;
    max-width: 200px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #2e7d32;
    line-height: 1.3;
    word-break: break-all;
}
</style>